import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CctpService } from '@trace/connector';
import { useOdoo } from '../../context/OdooContext.js';
import { useCctpProjects } from '../../hooks/useCctpProject.js';
import type { Project } from '../../types.js';

function ProjectCard({ project, onClick }: { project: Project; onClick: () => void }) {
  const dims = (project.dimensions_actives as string[] | undefined) ?? [];
  return (
    <button
      onClick={onClick}
      className="text-left bg-white rounded-2xl shadow-card p-6 hover:shadow-lg transition-shadow border border-transparent hover:border-brand/20 w-full"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-bold text-ink text-base">{project.name}</h3>
          <p className="text-xs text-ink-3 mt-0.5">{project.maitre_ouvrage}</p>
        </div>
        <span className="text-xs font-mono bg-brand-soft text-brand-dark px-2 py-0.5 rounded-md">
          Indice {project.indice ?? 'A'}
        </span>
      </div>

      <div className="flex gap-4 text-xs text-ink-2 mb-4">
        {project.puissance_pv_kwc > 0 && <span>☀️ {project.puissance_pv_kwc} kWc</span>}
        {project.capacite_bess_kwh > 0 && <span>🔋 {project.capacite_bess_kwh} kWh</span>}
        {project.adresse && <span>📍 {project.adresse}</span>}
      </div>

      {dims.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {dims.map(d => (
            <span key={d} className="text-xs bg-brand-soft text-brand-dark px-2 py-0.5 rounded-md font-medium">{d}</span>
          ))}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-line flex justify-between items-center">
        <span className="text-xs text-ink-3">{project.date_debut ?? '—'}</span>
        <span className="text-xs font-semibold text-ink-2">
          {project.estimation_total_ht ? `${project.estimation_total_ht.toLocaleString('fr-FR')} € HT` : ''}
        </span>
      </div>
    </button>
  );
}

export default function DashboardScreen() {
  const { client, logout } = useOdoo();
  const navigate = useNavigate();
  const { projects, loading, error } = useCctpProjects(client);
  const [creating, setCreating] = useState(false);

  async function handleNewProject() {
    if (!client) return;
    setCreating(true);
    try {
      const svc = new CctpService(client);
      const id = await svc.createProject({ name: 'Nouveau projet', indice: 'A', credits_ia: 230 });
      navigate(`/projects/${id}`);
    } catch {
      alert('Erreur lors de la création du projet');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg">
      <header className="bg-white border-b border-line px-8 py-4 flex items-center justify-between">
        <div className="text-xl font-extrabold bg-gradient-to-br from-brand to-indigo-400 bg-clip-text text-transparent">
          TRACE Energy AI
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleNewProject}
            disabled={creating}
            className="px-4 py-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60"
          >
            {creating ? 'Création…' : '+ Nouveau projet'}
          </button>
          <button onClick={logout} className="text-sm text-ink-3 hover:text-ink transition-colors">
            Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-8">
        <h1 className="text-2xl font-bold text-ink mb-6">Mes projets</h1>

        {loading && (
          <div className="text-center py-20 text-ink-3">Chargement…</div>
        )}

        {error && (
          <div className="bg-red-soft text-red rounded-xl p-4 text-sm">{error.message}</div>
        )}

        {!loading && projects.length === 0 && (
          <div className="text-center py-20">
            <p className="text-ink-3 mb-4">Aucun projet. Créez votre premier CCTP.</p>
            <button
              onClick={handleNewProject}
              className="px-6 py-3 bg-brand text-white font-semibold rounded-xl"
            >
              + Nouveau projet
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map(p => (
            <ProjectCard key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />
          ))}
        </div>
      </main>
    </div>
  );
}
